import {
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import React, { useEffect } from 'react';
import { SelectProps } from '@material-ui/core/Select/Select';

/**
 * Interface pour les props du composant DbDocumentSelect
 */
export interface DbDocumentSelectProps<T>
  extends Omit<SelectProps, 'onChange'> {
  onChange?: (value?: T) => any;
  docs?: T[];
  selectedId?: string;
  autoSelectFirst?: boolean;
  allowNone?: boolean;
  labelForEmpty?: string;
}

/**
 * Composant de sélection de document depuis la base de données
 * 
 * Ce composant permet de :
 * - Sélectionner un document depuis une liste de documents
 * - Afficher un indicateur de chargement pendant le chargement des données
 * - Sélectionner automatiquement le premier élément si configuré
 * - Permettre la sélection "aucun" si configuré
 * 
 * Logique métier :
 * - Générique pour tous types de documents avec displayName
 * - Sélection automatique du premier élément si autoSelectFirst
 * - Gestion des états de chargement avec CircularProgress
 * - Interface utilisateur cohérente avec Material-UI
 * 
 * @param onChange Fonction appelée lors du changement de sélection
 * @param docs Liste des documents disponibles
 * @param selectedId ID du document actuellement sélectionné
 * @param autoSelectFirst Sélectionner automatiquement le premier élément
 * @param allowNone Permettre la sélection "aucun"
 * @param labelForEmpty Label pour l'option "aucun"
 * @param className Classe CSS optionnelle
 * @param selectProps Props supplémentaires pour le Select Material-UI
 * @returns Composant React de sélection de document
 */
export const DbDocumentSelect = <
  T extends CRM.DbDocument & { displayName: string }
>({
  onChange,
  docs,
  selectedId,
  autoSelectFirst,
  allowNone,
  className,
  labelForEmpty,
  ...selectProps
}: DbDocumentSelectProps<T>) => {
  /**
   * Effect pour la sélection automatique du premier élément
   */
  useEffect(() => {
    if (autoSelectFirst && !selectedId && docs && docs.length > 0) {
      onChange && onChange(docs[0]);
    }
  }, [autoSelectFirst, onChange, selectedId, docs]);

  if (!docs) {
    return <CircularProgress size="1em" />;
  }

  return (
    <FormControl
      variant="outlined"
      className={className}
      fullWidth={selectProps.fullWidth}
    >
      <InputLabel id="Group-select-label">{selectProps.label}</InputLabel>
      <Select
        {...selectProps}
        displayEmpty={allowNone ? true : false}
        labelId="Doc-select-label"
        value={selectedId || ''}
        onChange={e => {
          const newSelected = docs.find(doc => doc._id === e.target.value);

          if (newSelected && onChange) {
            onChange(newSelected);
          } else if (allowNone && onChange) {
            onChange();
          }
        }}
      >
        {allowNone && <MenuItem value="">{labelForEmpty || 'Tous'}</MenuItem>}
        {docs.map(doc => (
          <MenuItem value={doc._id} key={doc._id}>
            {doc.displayName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Interface pour les props du composant DbMultiDocumentSelect
 */
export interface DbMultiDocumentSelectProps<T>
  extends Omit<SelectProps, 'onChange'> {
  onChange?: (value?: T[]) => any;
  docs?: T[];
  selectedIds?: string[];
  allowNone?: boolean;
}

/**
 * Composant de sélection multiple de documents depuis la base de données
 * 
 * Ce composant permet de :
 * - Sélectionner plusieurs documents depuis une liste
 * - Afficher un indicateur de chargement pendant le chargement des données
 * - Permettre la sélection "aucun" si configuré
 * - Afficher les éléments sélectionnés avec des checkboxes
 * 
 * Logique métier :
 * - Générique pour tous types de documents avec displayName
 * - Sélection multiple avec checkboxes
 * - Affichage des éléments sélectionnés séparés par des virgules
 * - Interface utilisateur cohérente avec Material-UI
 * 
 * @param onChange Fonction appelée lors du changement de sélection
 * @param docs Liste des documents disponibles
 * @param selectedIds IDs des documents actuellement sélectionnés
 * @param allowNone Permettre la sélection "aucun"
 * @param className Classe CSS optionnelle
 * @param selectProps Props supplémentaires pour le Select Material-UI
 * @returns Composant React de sélection multiple de documents
 */
export const DbMultiDocumentSelect = <
  T extends CRM.DbDocument & { displayName: string }
>({
  onChange,
  docs,
  selectedIds,
  allowNone,
  className,
  ...selectProps
}: DbMultiDocumentSelectProps<T>) => {
  if (!docs) {
    return <CircularProgress size="1em" />;
  }

  const selected = selectedIds || [];

  return (
    <FormControl
      variant="outlined"
      className={className}
      fullWidth={selectProps.fullWidth}
    >
      <InputLabel id="Group-select-label">{selectProps.label}</InputLabel>
      <Select
        {...selectProps}
        multiple
        labelId="Doc-select-label"
        value={selected}
        onChange={e => {
          const newSelected = docs.filter(doc =>
            (e.target.value as string[]).includes(doc._id),
          );

          if (newSelected && onChange) {
            onChange(newSelected);
          } else if (allowNone && onChange) {
            onChange();
          }
        }}
        renderValue={selected => {
          return (selected as string[])
            .map(item => docs?.find(i => i._id === item)?.displayName)
            .join(', ');
        }}
        MenuProps={MenuProps}
      >
        {allowNone && <MenuItem value="">&nbsp;</MenuItem>}
        {docs.map(doc => (
          <MenuItem value={doc._id} key={doc._id}>
            <Checkbox checked={selected.includes(doc._id)} />
            {doc.displayName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Configuration des propriétés du menu déroulant
 */
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
